import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { Quaternion, Vector3 } from 'three';
import { SCALE } from 'utils/3d';
import { ZoneConcealedDuctLayout } from 'utils/concealed-duct-geometry';
import {
  CONCEALED_DUCT_RUN_WORLD_HEIGHT,
  getConcealedDuctRunWorldHeight
} from 'utils/concealed-duct-placement';
import {
  buildDuctRunPathPx,
  ductRunEndpointKey,
  DuctRunKind,
  DuctRunRef,
  ductRunRefKey,
  ductRunWaypointKey,
  PointPx,
  snapPxToGrid
} from 'utils/duct-runs';

export type DuctPoint3DGeometry = {
  key: string;
  runRef: DuctRunRef;
  pointUniqueId: string | null;
  px: PointPx;
  x: number;
  y: number;
  z: number;
};

export type DuctSegment3DGeometry = {
  key: string;
  index: number;
  start: [number, number, number];
  end: [number, number, number];
};

export type DuctRun3DGeometry = {
  key: string;
  runRef: DuctRunRef;
  kind: DuctRunKind;
  removable: boolean;
  structureSpaceId: string | null;
  endpointPx: PointPx;
  pinReturnEndpointPx?: PointPx;
  waypointsPx: Array<{ uniqueId: string; x: number; y: number }>;
  autoWaypoints: boolean;
  points: DuctPoint3DGeometry[];
  segments: DuctSegment3DGeometry[];
};

export type ZoneDuct3DGeometry = {
  zoneUniqueId: string;
  originWorld: [number, number, number];
  runs: DuctRun3DGeometry[];
};

export function ductPxToWorld(
  px: PointPx,
  yOffset: number,
  runWorldHeight: number = CONCEALED_DUCT_RUN_WORLD_HEIGHT
): [number, number, number] {
  return [
    (px.x / STRUCTURE_GRID_SIZE) * SCALE,
    yOffset + runWorldHeight,
    (px.y / STRUCTURE_GRID_SIZE) * SCALE
  ];
}

export function ductWorldToPx(worldX: number, worldZ: number): PointPx {
  return {
    x: (worldX / SCALE) * STRUCTURE_GRID_SIZE,
    y: (worldZ / SCALE) * STRUCTURE_GRID_SIZE
  };
}

export function resolveDuctPointDrag(
  anchorPosition: Vector3,
  yOffset: number,
  clampPx: (proposedPx: PointPx) => PointPx | null,
  runWorldHeight: number = CONCEALED_DUCT_RUN_WORLD_HEIGHT
): PointPx {
  let px = snapPxToGrid(ductWorldToPx(anchorPosition.x, anchorPosition.z));
  px = clampPx(px) ?? px;

  const [worldX, worldY, worldZ] = ductPxToWorld(px, yOffset, runWorldHeight);
  anchorPosition.set(worldX, worldY, worldZ);

  return px;
}

const DUCT_DRAG_EPSILON = 0.001;

export function isDuctPointDragUnchanged(
  startPx: PointPx,
  px: PointPx
): boolean {
  return (
    Math.abs(px.x - startPx.x) < DUCT_DRAG_EPSILON &&
    Math.abs(px.y - startPx.y) < DUCT_DRAG_EPSILON
  );
}

export type SegmentTransform = {
  position: [number, number, number];
  quaternion: Quaternion;
  length: number;
};

export function computeSegmentTransform(
  start: [number, number, number],
  end: [number, number, number]
): SegmentTransform | null {
  const [x1, y1, z1] = start;
  const [x2, y2, z2] = end;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dz = z2 - z1;
  const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (length < 0.001) return null;

  const direction = new Vector3(dx, dy, dz).normalize();
  const quaternion = new Quaternion().setFromUnitVectors(
    new Vector3(0, 1, 0),
    direction
  );

  return {
    position: [(x1 + x2) / 2, (y1 + y2) / 2, (z1 + z2) / 2],
    quaternion,
    length
  };
}

type BuildRunArgs = {
  runRef: DuctRunRef;
  originPx: PointPx;
  waypointsPx: Array<{ uniqueId: string; x: number; y: number }>;
  autoWaypoints?: boolean;
  endpointPx: PointPx;
  structureSpaceId: string | null;
  removable: boolean;
  yOffset: number;
  runWorldHeight: number;
  pinReturnEndpointPx?: PointPx;
};

function buildRunGeometry({
  runRef,
  originPx,
  waypointsPx,
  autoWaypoints = false,
  endpointPx,
  structureSpaceId,
  removable,
  yOffset,
  runWorldHeight,
  pinReturnEndpointPx
}: BuildRunArgs): DuctRun3DGeometry {
  const runKey = ductRunRefKey(runRef);
  const pathPx = buildDuctRunPathPx(originPx, waypointsPx, endpointPx);

  const points: DuctPoint3DGeometry[] = waypointsPx.map((point) => {
    const [x, y, z] = ductPxToWorld(point, yOffset, runWorldHeight);
    return {
      key: ductRunWaypointKey(runRef, point.uniqueId),
      runRef,
      pointUniqueId: point.uniqueId,
      px: { x: point.x, y: point.y },
      x,
      y,
      z
    };
  });

  const [endpointX, endpointY, endpointZ] = ductPxToWorld(
    endpointPx,
    yOffset,
    runWorldHeight
  );
  points.push({
    key: ductRunEndpointKey(runRef),
    runRef,
    pointUniqueId: null,
    px: { x: endpointPx.x, y: endpointPx.y },
    x: endpointX,
    y: endpointY,
    z: endpointZ
  });

  const segments: DuctSegment3DGeometry[] = [];
  for (let index = 0; index < pathPx.length - 1; index += 1) {
    segments.push({
      key: `${runKey}::segment::${index}`,
      index,
      start: ductPxToWorld(pathPx[index], yOffset, runWorldHeight),
      end: ductPxToWorld(pathPx[index + 1], yOffset, runWorldHeight)
    });
  }

  return {
    key: runKey,
    runRef,
    kind: runRef.kind,
    removable,
    structureSpaceId,
    endpointPx: { x: endpointPx.x, y: endpointPx.y },
    pinReturnEndpointPx,
    waypointsPx,
    autoWaypoints,
    points,
    segments
  };
}

export function computeZoneDuct3DGeometry(
  layout: ZoneConcealedDuctLayout,
  yOffset: number
): ZoneDuct3DGeometry | null {
  const { zoneUniqueId, condenserUniqueId } = layout;
  if (!zoneUniqueId) return null;

  const runWorldHeight = getConcealedDuctRunWorldHeight(layout.placement);
  const runs: DuctRun3DGeometry[] = [];

  const passiveSpaceIds = new Set(
    layout.branches
      .filter((branch) => branch.servedPassively)
      .map((branch) => branch.spaceUniqueId)
  );

  layout.branches.forEach((branch) => {
    if (
      !branch.sameLevelAsOrigin ||
      !branch.centroidPx ||
      branch.servedPassively
    ) {
      return;
    }
    runs.push(
      buildRunGeometry({
        runRef: {
          zoneUniqueId,
          condenserUniqueId,
          kind: 'supply',
          spaceUniqueId: branch.spaceUniqueId,
          runUniqueId: null
        },
        originPx: layout.originPx,
        waypointsPx: branch.waypointsPx,
        autoWaypoints: branch.autoWaypoints,
        endpointPx: branch.centroidPx,
        structureSpaceId: branch.structureSpaceId,
        removable: false,
        yOffset,
        runWorldHeight
      })
    );
  });

  layout.additionalSupplyRuns.forEach((run) => {
    if (!run.sameLevelAsOrigin || passiveSpaceIds.has(run.spaceUniqueId)) {
      return;
    }
    runs.push(
      buildRunGeometry({
        runRef: {
          zoneUniqueId,
          condenserUniqueId,
          kind: 'supply',
          spaceUniqueId: run.spaceUniqueId,
          runUniqueId: run.runUniqueId
        },
        originPx: layout.originPx,
        waypointsPx: run.waypointsPx,
        autoWaypoints: run.autoWaypoints,
        endpointPx: run.endpointPx,
        structureSpaceId: run.structureSpaceId,
        removable: true,
        yOffset,
        runWorldHeight
      })
    );
  });

  const ret = layout.returnRun;
  if (ret && ret.sameLevelAsOrigin) {
    runs.push(
      buildRunGeometry({
        runRef: {
          zoneUniqueId,
          condenserUniqueId,
          kind: 'return',
          runUniqueId: null
        },
        originPx: layout.originPx,
        waypointsPx: ret.waypointsPx,
        autoWaypoints: ret.autoWaypoints,
        endpointPx: ret.endpointPx,
        structureSpaceId: ret.containingSpaceId,
        removable: false,
        yOffset,
        runWorldHeight,
        pinReturnEndpointPx: ret.endpointPx
      })
    );
  }

  layout.additionalReturnRuns.forEach((run) => {
    if (!run.sameLevelAsOrigin) return;
    runs.push(
      buildRunGeometry({
        runRef: {
          zoneUniqueId,
          condenserUniqueId,
          kind: 'return',
          runUniqueId: run.runUniqueId
        },
        originPx: layout.originPx,
        waypointsPx: run.waypointsPx,
        autoWaypoints: run.autoWaypoints,
        endpointPx: run.endpointPx,
        structureSpaceId: run.containingSpaceId,
        removable: true,
        yOffset,
        runWorldHeight
      })
    );
  });

  if (runs.length === 0) return null;

  return {
    zoneUniqueId,
    originWorld: ductPxToWorld(layout.originPx, yOffset, runWorldHeight),
    runs
  };
}

export type DuctDragOverride = {
  runRef: DuctRunRef;
  pointUniqueId: string | null;
  px: PointPx;
};

function runMatchesRef(candidate: DuctRunRef, target: DuctRunRef): boolean {
  return (
    candidate.zoneUniqueId === target.zoneUniqueId &&
    candidate.kind === target.kind &&
    candidate.runUniqueId === target.runUniqueId &&
    (candidate.kind === 'return' ||
      candidate.spaceUniqueId === target.spaceUniqueId)
  );
}

function overrideWaypoints(
  waypointsPx: Array<{ uniqueId: string; x: number; y: number }>,
  override: DuctDragOverride
): Array<{ uniqueId: string; x: number; y: number }> {
  return waypointsPx.map((point) =>
    point.uniqueId === override.pointUniqueId
      ? { ...point, x: override.px.x, y: override.px.y }
      : point
  );
}

export function applyDuctDragOverride(
  layout: ZoneConcealedDuctLayout,
  override: DuctDragOverride | null
): ZoneConcealedDuctLayout {
  if (!override || layout.zoneUniqueId !== override.runRef.zoneUniqueId) {
    return layout;
  }

  const { runRef } = override;
  const isEndpoint = override.pointUniqueId === null;

  if (runRef.kind === 'supply' && runRef.runUniqueId === null) {
    return {
      ...layout,
      branches: layout.branches.map((branch) => {
        if (branch.spaceUniqueId !== runRef.spaceUniqueId) return branch;
        if (isEndpoint) {
          return { ...branch, centroidPx: { ...override.px } };
        }
        return {
          ...branch,
          waypointsPx: overrideWaypoints(branch.waypointsPx, override)
        };
      })
    };
  }

  if (runRef.kind === 'supply') {
    return {
      ...layout,
      additionalSupplyRuns: layout.additionalSupplyRuns.map((run) => {
        if (
          !runMatchesRef(
            {
              zoneUniqueId: layout.zoneUniqueId ?? '',
              kind: 'supply',
              spaceUniqueId: run.spaceUniqueId,
              runUniqueId: run.runUniqueId
            },
            runRef
          )
        ) {
          return run;
        }
        if (isEndpoint) {
          return { ...run, endpointPx: { ...override.px } };
        }
        return {
          ...run,
          waypointsPx: overrideWaypoints(run.waypointsPx, override)
        };
      })
    };
  }

  if (runRef.runUniqueId === null) {
    if (!layout.returnRun) return layout;
    if (isEndpoint) {
      return {
        ...layout,
        returnRun: { ...layout.returnRun, endpointPx: { ...override.px } }
      };
    }
    return {
      ...layout,
      returnRun: {
        ...layout.returnRun,
        waypointsPx: overrideWaypoints(layout.returnRun.waypointsPx, override)
      }
    };
  }

  return {
    ...layout,
    additionalReturnRuns: layout.additionalReturnRuns.map((run) => {
      if (run.runUniqueId !== runRef.runUniqueId) return run;
      if (isEndpoint) {
        return { ...run, endpointPx: { ...override.px } };
      }
      return {
        ...run,
        waypointsPx: overrideWaypoints(run.waypointsPx, override)
      };
    })
  };
}
